import { shallowEqual } from 'react-redux';

import { Grid, PageContent, Table } from '@zillow/constellation';

import { AppSelectors } from '../../../selectors';
import { useSelector } from '../../../selectors/useSelector';

import PropertiesConstants from '../../../constants/PropertiesConstants';

import Bone from '../../../components/Bone';
import PropertyRowSkeleton from '../../../components/skeletons/PropertyRowSkeleton';
import PropertyCardSkeleton from '../../../components/skeletons/PropertyCardSkeleton';
import SpinnerContainer from '../../../components/SpinnerContainer';
import { StyledTableContainer } from '../../../components/properties/PropertiesViewList/StyledComponents';

const PropertiesPageSkeleton = () => {
  const { canAccessDashboardRedesign } = useSelector((state) => {
    return { canAccessDashboardRedesign: state.app.canAccessDashboardRedesign };
  }, shallowEqual);

  const isScreenXlGTE = useSelector((state) => AppSelectors.makeIsScreenXlGTE(state));
  const isScreenLgGTE = useSelector((state) => AppSelectors.makeIsScreenLgGTE(state));
  const isScreenMobile = useSelector((state) => AppSelectors.makeIsScreenMobile(state));
  const isScreenLg = useSelector((state) => AppSelectors.makeIsScreenLg(state));
  const isEnterpriseCustomer = useSelector((state) => state.user.isEnterpriseCustomer);
  const isScreenSizeComputed = useSelector((state) => state.app.isScreenSizeComputed);
  const { viewType } = useSelector((state) => state.properties.propertiesFilters);

  const maxHeaderCells = isScreenLg ? 6 : 7;
  const headersForEnterpriseOrRegular = isEnterpriseCustomer ? maxHeaderCells : 5;

  const headersForDesktopOrTablet = isScreenLgGTE ? headersForEnterpriseOrRegular : 3;
  const finalHeaderCount = isScreenMobile ? 2 : headersForDesktopOrTablet;

  const skeletonHeaderToShow = [...Array(finalHeaderCount)];
  const skeletonsToShow = [...Array(8)];

  const renderSkeleton = canAccessDashboardRedesign === false && isScreenSizeComputed;

  const getGridColumnStart = (idx: number) => {
    if (isScreenXlGTE) {
      switch (idx % 3) {
        case 0:
          return 1;
        case 1:
          return 5;
        case 2:
          return 9;
        default:
          return 1;
      }
    }
    if (isScreenMobile) {
      return 1;
    }
    return idx % 2 === 0 ? 1 : 7;
  };

  const renderSkeletons = () => {
    if (renderSkeleton && viewType === PropertiesConstants.PROPERTIES_VIEW_TYPE.LIST) {
      return (
        <StyledTableContainer>
          <Table appearance="horizontal" aria-label="Medium-sized grid table" size="md">
            <Table.Header>
              <Table.Row>
                {skeletonHeaderToShow.map((_, idx) => (
                  <Table.HeaderCell key={idx}>
                    {(!isScreenLgGTE || idx !== 0) && <Bone type="line" dark={true} />}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {skeletonsToShow.map((_, idx) => (
                <PropertyRowSkeleton key={idx} />
              ))}
            </Table.Body>
          </Table>
        </StyledTableContainer>
      );
    }

    if (renderSkeleton && viewType === PropertiesConstants.PROPERTIES_VIEW_TYPE.GRID) {
      return (
        <Grid display="grid" gridTemplateColumns="repeat(12, 1fr)" gap="sm" className="PropertiesDisplay-listings">
          {skeletonsToShow.map((_, idx) => (
            <Grid
              gridColumnStart={getGridColumnStart(idx)}
              gridColumnEnd={{ xs: 'span 12', md: 'span 6', xl: 'span 4' }}
              key={idx}
            >
              <PropertyCardSkeleton />
            </Grid>
          ))}
        </Grid>
      );
    }

    return <SpinnerContainer height="300px" />;
  };

  return (
    <PageContent className="PropertiesPageSkeleton PropertiesPage-display-container">{renderSkeletons()}</PageContent>
  );
};

export default PropertiesPageSkeleton;
